/**
 * Smaller WebP copies of an uploaded picture, made when it is uploaded and stored beside it, so a
 * page can let each screen load the size it shows instead of the full original. Copies follow
 * their original everywhere: the same authorization, and the same publication to the public CDN.
 */

/** The widths a picture is resized to. A narrower picture keeps its own width as its largest copy. */
export const resizedWidths = [480, 960, 1440, 1920, 2560] as const;

/** Widths to make for a picture `width` pixels wide: never enlarged, and no step within 10% of its own width. */
export function resizedWidthsFor(width: number): number[] {
  if (!Number.isInteger(width) || width < 1) return [];
  const steps = resizedWidths.filter((step) => step < width * 0.9);
  return width <= resizedWidths.at(-1)! ? [...steps, width] : steps;
}

/** Where a resized copy is stored: beside its original, named by its width. */
export function resizedKey(original: string, width: number) {
  return `${original}/w${width}.webp`;
}

/** The width a resized copy's key names, or null for any other key. */
export function resizedKeyWidth(key: string, original: string): number | null {
  if (!key.startsWith(`${original}/`)) return null;
  const width = Number(/^w([1-9]\d{0,3})\.webp$/.exec(key.slice(original.length + 1))?.[1]);
  return width && width <= resizedWidths.at(-1)! ? width : null;
}

/** A picture's public address, with its resized copies when it has them. */
export type PublicImage = { src: string; srcset?: string };

/** The srcset listing a picture's resized copies, narrowest first. */
export function resizedSrcset(copies: { url: string; width: number }[]) {
  return copies.length
    ? [...copies]
        .sort((a, b) => a.width - b.width)
        .map((copy) => `${copy.url} ${copy.width}w`)
        .join(", ")
    : undefined;
}

/** Post pictures served by an access-checked route: a cover, or a public post's attached picture. */
const resizingRoute = /^\/api\/(?:media|playable-media\/artwork|shop\/assets)\/[a-zA-Z0-9-]+$/;

/**
 * The srcset for a post picture whose route resizes it. The route answers ?w= with the picture's
 * nearest copy, or its original when it has none, so the srcset names each step without knowing
 * which copies exist. Use it only where the card sets the picture's size (a width and an aspect
 * ratio), since a step can name more pixels than the picture has.
 */
export function routeSrcset(src: string | null | undefined) {
  return src && resizingRoute.test(src)
    ? resizedWidths
        .slice(0, 4)
        .map((width) => `${src}?w=${width} ${width}w`)
        .join(", ")
    : undefined;
}

/**
 * The block size of upscaled pixel art: the k for which the picture is k×k squares of one color
 * each, so its own resolution is width/k by height/k. Read from where colors change along sampled
 * rows and columns: in upscaled art nearly every change falls on the grid. Photos, art drawn at
 * its own size and noisy JPEGs answer 1. `tolerance` is how far a channel may drift within one
 * square (a JPEG's noise).
 */
export function pixelArtScale(
  rgba: ArrayLike<number>,
  width: number,
  height: number,
  tolerance = 0,
): number {
  const differs = (a: number, b: number) => {
    for (let channel = 0; channel < 4; channel++)
      if (Math.abs(rgba[a + channel]! - rgba[b + channel]!) > tolerance) return true;
    return false;
  };
  const across: number[] = [];
  const down: number[] = [];
  const lines = 48;
  for (let line = 0; line < Math.min(height, lines); line++) {
    const y = Math.floor(((line + 0.5) * height) / Math.min(height, lines));
    for (let x = 1; x < width; x++)
      if (differs((y * width + x - 1) * 4, (y * width + x) * 4)) across.push(x);
  }
  for (let line = 0; line < Math.min(width, lines); line++) {
    const x = Math.floor(((line + 0.5) * width) / Math.min(width, lines));
    for (let y = 1; y < height; y++)
      if (differs(((y - 1) * width + x) * 4, (y * width + x) * 4)) down.push(y);
  }
  const total = across.length + down.length;
  const sampled = Math.min(height, lines) * (width - 1) + Math.min(width, lines) * (height - 1);
  // Too few changes to read a grid from, or more than any grid of 2 or more could hold (a photo).
  if (total < 64 || total > sampled / 2) return 1;
  // The share of changes on the most common offset of a k grid, along one direction.
  const onGrid = (positions: number[], k: number) => {
    const counts: number[] = Array.from({ length: k }, () => 0);
    for (const position of positions) counts[position % k]!++;
    return Math.max(...counts);
  };
  // The largest grid nearly every change sits on; a grid twice the true one holds only half.
  for (let k = 64; k >= 2; k--) {
    if (width / k < 8 || height / k < 8) continue;
    if ((onGrid(across, k) + onGrid(down, k)) / total >= 0.97) return k;
  }
  return 1;
}

/**
 * The widths of an upscaled pixel art picture's copies: whole multiples of its own resolution, so
 * each copy keeps every square the same size, nearest each resized step and never wider than the
 * original. Its own resolution comes first when it is a useful size.
 */
export function pixelArtWidths(width: number, scale: number): number[] {
  const own = Math.round(width / scale);
  const widths = resizedWidthsFor(width).map((step) => own * Math.max(1, Math.round(step / own)));
  return [...new Set([...(own >= 64 ? [own] : []), ...widths])]
    .filter((copy) => copy < width && copy <= resizedWidths.at(-1)!)
    .slice(0, resizedWidths.length);
}

/** The width a ?w= asks for when it names a step; anything else asks for the original. */
export function requestedWidth(value: string | null | undefined): number | null {
  const width = Number(value);
  return resizedWidths.some((step) => step === width) ? width : null;
}

/** The sizes a picture loaded at once announces. Its preload must repeat them exactly, or the
 * browser fetches the picture twice. */
export const eagerImageSizes = "100vw";

/** A lazy picture's sizes: its laid-out width where the browser supports it, else the screen's. */
export const lazyImageSizes = "auto, 100vw";

/**
 * A repeating list's picture sizes, from the columns it names: a phone shows `mobileColumns` (one
 * unless set) per row, a wider screen `columns`. A list without columns keeps the browser's own
 * measure, since naming too small a size would blur its pictures.
 */
export function repeaterImageSizes(style?: { columns?: number; mobileColumns?: number }) {
  if (!style?.columns) return undefined;
  const phone = Math.ceil(100 / (style.mobileColumns ?? 1));
  return `(max-width: 760px) ${phone}vw, ${Math.ceil(100 / style.columns)}vw`;
}

/**
 * Whether a picture's bytes can be resized: a still PNG, JPEG or WebP. GIFs and animated PNG or
 * WebP files keep only their original, since a resized copy would hold one frame.
 */
export function resizableImage(bytes: Uint8Array) {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return true;
  if (isPng(bytes)) {
    // An animated PNG names its frames (acTL) before its first image data (IDAT).
    for (let offset = 8; offset + 8 <= bytes.length;) {
      const type = ascii(bytes, offset + 4, 4);
      if (type === "acTL") return false;
      if (type === "IDAT") return true;
      offset += 12 + new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
    }
    return false;
  }
  if (!isWebp(bytes)) return false;
  // An extended WebP (VP8X) carries its animation flag in the second bit of its first byte.
  return ascii(bytes, 12, 4) !== "VP8X" || (bytes[20]! & 0x02) === 0;
}

/** A WebP picture's width in pixels, read from its header, or null when it isn't a WebP. */
export function webpWidth(bytes: Uint8Array): number | null {
  if (!isWebp(bytes) || bytes.length < 30) return null;
  const chunk = ascii(bytes, 12, 4);
  if (chunk === "VP8 ") {
    if (bytes[23] !== 0x9d || bytes[24] !== 0x01 || bytes[25] !== 0x2a) return null;
    return (bytes[26]! | (bytes[27]! << 8)) & 0x3fff;
  }
  if (chunk === "VP8L")
    return bytes[20] === 0x2f ? 1 + (bytes[21]! | ((bytes[22]! & 0x3f) << 8)) : null;
  if (chunk === "VP8X") return 1 + (bytes[24]! | (bytes[25]! << 8) | (bytes[26]! << 16));
  return null;
}

function isPng(bytes: Uint8Array) {
  return bytes[0] === 0x89 && ascii(bytes, 1, 3) === "PNG";
}

function isWebp(bytes: Uint8Array) {
  return ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP";
}

function ascii(bytes: Uint8Array, start: number, length: number) {
  return String.fromCharCode(...bytes.subarray(start, start + length));
}
